import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import PlansSection, { type PlanKey } from '../components/PlansSection'
import TrialCTASection from '../components/TrialCTASection'
import { fetchPublicConfig, createCheckoutSession, checkSubscription } from '../lib/api'
import { useLiff } from '../hooks/useLiff'

// Assets
import smartPhoneImg from '../assets/smart-phone.png'
import starImg from '../assets/star.png'
import generationEasy1 from '../assets/generation-easy-1.png'
import generationEasy2 from '../assets/generation-easy-2.png'
import generationEasy3 from '../assets/generation-easy-3.png'
import generationEasy4 from '../assets/generation-easy-4.png'
import tryOnExplanation1 from '../assets/try-on-explanation-1.png'
import tryOnExplanation2 from '../assets/try-on-explanation-2.png'
import tryOnExplanation3 from '../assets/try-on-explanation-3.png'

export default function IndexPage() {
  const [toast, setToast] = useState<{ type: 'success' | 'cancel'; message: string } | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>('standard')
  const [hasEverSubscribed, setHasEverSubscribed] = useState(false)
  const [lineUserId, setLineUserId] = useState('')
  const [liffId, setLiffId] = useState('')
  const autoCheckoutTriggeredRef = useRef(false)

  // Check URL params
  const { status, billingFlag } = useMemo(() => {
    if (typeof window === 'undefined') return { status: null, billingFlag: false }
    const u = new URL(window.location.href)
    const s = u.searchParams.get('status')
    const valToBool = (v: string | null) => {
      const val = (v || '').toLowerCase()
      return val === 'true' || val === '1' || val === 'yes'
    }
    let billing = valToBool(u.searchParams.get('billing'))
    // Check liff.state for billing flag
    if (!billing) {
      try {
        const state = u.searchParams.get('liff.state')
        if (state) {
          const decoded = decodeURIComponent(state)
          const qs = decoded.startsWith('?') ? decoded.slice(1) : decoded
          const su = new URLSearchParams(qs)
          billing = valToBool(su.get('billing'))
        }
      } catch {
        // Ignore
      }
    }
    return { status: s, billingFlag: billing }
  }, [])

  // Fetch LIFF ID from config
  useEffect(() => {
    let cancelled = false
    fetchPublicConfig()
      .then((config) => {
        if (!cancelled && config.liffRichmenuAppId) {
          setLiffId(config.liffRichmenuAppId)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[config] fetchPublicConfig failed', err)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Check for session user ID
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('user_id') || ''
      if (stored) {
        setLineUserId(stored)
      }
    } catch {
      // Ignore
    }
  }, [])

  // LIFF integration - only in LINE client
  const isLineClient = useMemo(() => {
    if (typeof window === 'undefined') return false
    return /Line\//i.test(navigator.userAgent || '')
  }, [])

  const { userId: liffUserId, isLoading: liffLoading, error: liffError } = useLiff({
    liffId: liffId,
    autoLogin: isLineClient && !lineUserId,
  })

  // Update user ID from LIFF
  useEffect(() => {
    if (!liffUserId) return
    if (liffUserId !== lineUserId) {
      setLineUserId(liffUserId)
      try {
        sessionStorage.setItem('user_id', liffUserId)
      } catch {
        // Ignore
      }
    }
  }, [liffUserId, lineUserId])

  // Set ticket plan if billing flag
  useEffect(() => {
    if (billingFlag) {
      setSelectedPlan('ticket10')
    }
  }, [billingFlag])

  // Checkout handler
  const startCheckout = useCallback(async () => {
    try {
      const trimmedUserId = lineUserId.trim()
      if (!trimmedUserId) {
        setToast({ type: 'cancel', message: 'LINEユーザーIDが取得できませんでした' })
        return
      }
      setIsLoading(true)
      const result = await createCheckoutSession(trimmedUserId, selectedPlan)
      if (!result.url) {
        throw new Error(result.error || '購入手続きに進めませんでした')
      }
      window.location.href = result.url
    } catch (e) {
      console.error('[billing] checkout error', e)
      setToast({ type: 'cancel', message: e instanceof Error ? e.message : 'エラーが発生しました' })
    } finally {
      setIsLoading(false)
    }
  }, [lineUserId, selectedPlan])

  // Auto-checkout for billing flag
  useEffect(() => {
    if (!billingFlag || !lineUserId || autoCheckoutTriggeredRef.current) return
    autoCheckoutTriggeredRef.current = true
    startCheckout()
  }, [billingFlag, lineUserId, startCheckout])

  // Check subscription status
  useEffect(() => {
    if (!lineUserId) return
    let cancelled = false
    checkSubscription(lineUserId)
      .then((result) => {
        if (!cancelled) {
          setHasEverSubscribed(!!(result.hasEver || result.active))
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[billing] checkSubscription failed', err)
        }
      })
    return () => {
      cancelled = true
    }
  }, [lineUserId])

  // Handle status toast
  useEffect(() => {
    if (status === 'success') {
      setToast({ type: 'success', message: 'お支払いに成功しました' })
    } else if (status === 'cancel') {
      setToast({ type: 'cancel', message: 'お支払いがキャンセルされました' })
    }

    // Clean URL
    if (status) {
      const u = new URL(window.location.href)
      u.searchParams.delete('status')
      window.history.replaceState({}, '', u.toString())
    }
  }, [status])

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 4000)
    return () => clearTimeout(t)
  }, [toast])

  // LIFF loading gate
  if (isLineClient && liffLoading) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-gray-700 px-6">
        <div className="max-w-sm w-full bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4 text-center">
          <p className="text-sm text-gray-700">LIFFからユーザーIDを取得しています…</p>
        </div>
      </main>
    )
  }

  if (isLineClient && liffError) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-gray-700 px-6">
        <div className="max-w-sm w-full bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4 text-center">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{liffError}</p>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md bg-green-500 px-4 py-2 text-white text-sm font-medium hover:bg-green-600"
            onClick={() => window.location.reload()}
          >
            再読み込み
          </button>
        </div>
      </main>
    )
  }

  return (
    <>
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[1000] px-4 py-2 rounded-md shadow-md text-white ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="min-h-screen bg-[#F8F8F8] pb-[120px] md:pb-0 font-roundedMplus">
        {/* Hero Section */}
        <section className="relative overflow-visible pt-64 md:pt-48 pb-[140px] md:pb-[200px]">
          <div aria-hidden className="absolute inset-0 bg-[#06C755]"></div>
          <svg
            aria-hidden
            className="absolute inset-x-0 bottom-0 w-full h-[140px] md:h-[200px] z-[1] pointer-events-none"
            viewBox="0 0 1440 200"
            preserveAspectRatio="none"
          >
            <path d="M0,0 C360,50 1080,50 1440,0 L1440,200 L0,200 Z" fill="white" />
          </svg>

          <div className="relative -mt-48 md:-mt-24 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="text-white relative z-10">
              <div className="mx-auto md:mx-0 text-center md:text-left">
                <p className="text-xm tracking-wide font-medium">プレミアムプランなら</p>
                <p className="mt-3 text-xxxm font-medium leading-tight whitespace-nowrap">いつでも使い放題</p>
              </div>
            </div>
          </div>

          <div className="pointer-events-none absolute inset-x-0 -bottom-10 md:-bottom-8 z-[2]">
            <img src={smartPhoneImg} alt="スマホ画面" className="w-full h-auto bg-transparent" />
          </div>
        </section>

        {/* Plans Section */}
        <PlansSection selectedPlan={selectedPlan} onChange={setSelectedPlan} />

        {/* CTA Section */}
        <TrialCTASection isLoading={isLoading} onClick={startCheckout} isReturning={hasEverSubscribed} />

        {/* Features Section */}
        <section className="bg-white">
          <div className="mx-auto max-w-6xl px-6 pt-10 pb-20">
            <h2 className="text-center text-[28px] md:text-[36px] font-medium text-black mb-6">
              画像生成を簡単に
            </h2>

            <div className="grid grid-cols-2 gap-x-6 md:gap-x-10 gap-y-12">
              <div className="flex flex-col items-center text-center">
                <img src={generationEasy1} alt="面倒な登録は必要なし" className="w-[120px] md:w-[160px] h-auto" />
                <div className="mt-4 text-[18px] md:text-[20px] font-medium text-black leading-tight">
                  面倒な登録は<br className="md:hidden" />必要なし
                </div>
                <div className="mt-2 text-sm md:text-base font-medium text-gray-500">友達登録だけでOK</div>
              </div>

              <div className="flex flex-col items-center text-center">
                <img src={generationEasy2} alt="難しいプロンプトは不要" className="w-[120px] md:w-[160px] h-auto" />
                <div className="mt-4 text-[18px] md:text-[20px] font-medium text-black leading-tight">
                  難しいプロンプトは<br className="md:hidden" />不要
                </div>
                <div className="mt-2 text-sm md:text-base font-medium text-gray-500">アップロードするだけ</div>
              </div>

              <div className="flex flex-col items-center text-center">
                <img src={generationEasy3} alt="日本語でわかりやすい" className="w-[120px] md:w-[160px] h-auto" />
                <div className="mt-4 text-[18px] md:text-[20px] font-medium text-black leading-tight">
                  日本語で<br className="md:hidden" />わかりやすい
                </div>
                <div className="mt-2 text-sm md:text-base font-medium text-gray-500">かんたんスタート</div>
              </div>

              <div className="flex flex-col items-center text-center">
                <img src={generationEasy4} alt="試着完了は通知でお知らせ" className="w-[120px] md:w-[160px] h-auto" />
                <div className="mt-4 text-[18px] md:text-[20px] font-medium text-black leading-tight">
                  試着完了は<br className="md:hidden" />通知でお知らせ
                </div>
                <div className="mt-2 text-sm md:text-base font-medium text-gray-500">待ち時間は不要</div>
              </div>
            </div>
          </div>
        </section>

        {/* How to Use Section */}
        <section className="bg-[#F8F8F8]">
          <div className="mx-auto max-w-6xl px-6 pt-10 pb-16">
            <h2 className="text-center text-[28px] md:text-[32px] font-medium text-black">出かけずに試着を</h2>

            {/* Step 1 */}
            <div className="mt-8 md:mt-10 flex flex-col items-center text-center">
              <div className="inline-flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-md border border-black/20 bg-white text-black text-base md:text-lg font-medium">
                1
              </div>
              <p className="mt-3 text-[18px] md:text-[20px] font-medium text-black">自分の全身写真をアップロード</p>
              <div className="mt-4 w-full">
                <div
                  className="mx-auto w-full max-w-[340px] md:max-w-[380px] bg-[#E9EFEE] rounded-2xl p-4 md:p-6 relative overflow-hidden"
                  style={{ aspectRatio: '5 / 6' }}
                >
                  <img
                    src={tryOnExplanation1}
                    alt="全身写真をアップロード"
                    className="absolute inset-[8%] object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="mt-10 md:mt-12 flex flex-col items-center text-center">
              <div className="inline-flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-md border border-black/20 bg-white text-black text-base md:text-lg font-medium">
                2
              </div>
              <p className="mt-3 text-[18px] md:text-[20px] font-medium text-black">カメラロールから衣装を選ぶ</p>
              <p className="mt-1 text-xs md:text-sm text-gray-500 font-medium">
                SNS・友達の投稿など、お気に入りのコーディネートを
                <br className="hidden md:block" />
                カメラロールから画像をアップロードしよう
              </p>
              <div className="mt-4 w-full">
                <div
                  className="mx-auto w-full max-w-[340px] md:max-w-[380px] bg-[#E9EFEE] rounded-2xl p-4 md:p-6 relative overflow-hidden"
                  style={{ aspectRatio: '5 / 6' }}
                >
                  <img
                    src={tryOnExplanation2}
                    alt="カメラロールから衣装を選ぶ"
                    className="absolute inset-[8%] object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="mt-10 md:mt-12 flex flex-col items-center text-center">
              <div className="inline-flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-md border border-black/20 bg-white text-black text-base md:text-lg font-medium">
                3
              </div>
              <p className="mt-3 text-[18px] md:text-[20px] font-medium text-black">試着</p>
              <p className="mt-1 text-xs md:text-sm text-gray-500 font-medium">うまく試着できたらシェアしよう！</p>
              <div className="mt-4 w-full">
                <div
                  className="mx-auto w-full max-w-[340px] md:max-w-[380px] bg-[#E9EFEE] rounded-2xl p-4 md:p-6 relative overflow-hidden"
                  style={{ aspectRatio: '5 / 6' }}
                >
                  <img src={tryOnExplanation3} alt="試着結果" className="absolute inset-[8%] object-contain" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Card Section */}
        <section className="mx-auto max-w-[720px] py-8">
          <div className="relative border-2 border-[#06C755] bg-[#06C755] p-1 md:p-8 overflow-hidden">
            <div className="flex justify-center">
              <h2 className="relative inline-block text-white text-2xl md:text-3xl font-medium">
                AI試着をカンタンに
                <img
                  src={starImg}
                  alt=""
                  className="absolute -top-1.5 -right-3 w-5 h-5 md:w-6 md:h-6 pointer-events-none select-none"
                />
              </h2>
            </div>
            <div className="relative mt-10 mx-auto max-w-[660px] bg-white p-4 md:p-6 shadow-[0_6px_20px_rgba(0,0,0,0.06)]">
              <svg
                aria-hidden
                className="absolute -top-7 left-0 w-full h-[56px] pointer-events-none"
                viewBox="0 0 1440 80"
                preserveAspectRatio="xMidYMid meet"
              >
                <path d="M0,80 A6500,6500 0 0 1 1440,80 L1440,0 L0,0 Z" fill="#06C755" />
              </svg>
              <p className="text-center text-[#06C755] text-[20px] md:text-[24px] font-semibold mb-4">
                自由に服を試そう
              </p>

              <PlansSection selectedPlan={selectedPlan} onChange={setSelectedPlan} compact />

              <div className="mt-4 flex items-center justify-center gap-3 md:gap-4">
                <span className="text-[#3E2F00] text-xs md:text-sm font-medium">月額プランのみ3日間無料</span>
                <span className="inline-block rounded-[6px] bg-[#F7E380] text-[#3E2F00] px-2 py-1 text-xs md:text-sm font-medium shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                  いつでもキャンセル可能
                </span>
              </div>

              <div className="mt-4 flex justify-center">
                <TrialCTASection
                  isLoading={isLoading}
                  onClick={startCheckout}
                  asInline
                  showBadges={false}
                  isReturning={hasEverSubscribed}
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mx-auto max-w-3xl px-6 pt-2 pb-10">
          <h2 className="text-center text-[28px] md:text-[36px] font-medium text-black">よくある質問</h2>
          <div className="mt-6 space-y-4">
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
              <div className="px-5 py-4">
                <p className="text-black text-base md:text-lg font-semibold">Q. 解約後はどうなりますか？</p>
              </div>
              <div className="border-t border-gray-100 px-5 py-4">
                <p className="text-black text-base md:text-lg font-semibold">A. 次回更新日までご利用可能</p>
                <p className="mt-2 text-xs md:text-sm text-gray-500 font-medium">
                  解約した場合、"解約手続き時点から次回決済時まで"ご利用いただけます。
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
              <div className="px-5 py-4">
                <p className="text-black text-base md:text-lg font-semibold">Q. 領収書は発行できますか？</p>
              </div>
              <div className="border-t border-gray-100 px-5 py-4">
                <p className="text-black text-base md:text-lg font-semibold">A. 発行できます</p>
                <p className="mt-2 text-xs md:text-sm text-gray-500 font-medium">いつでも発行いただけます。</p>
              </div>
            </div>
          </div>
        </section>

        {/* Notes Section */}
        <section className="mx-auto max-w-3xl px-6 pt-2 pb-12">
          <h2 className="text-center text-[28px] md:text-[36px] font-medium text-black">注意事項</h2>
          <div className="mt-6 bg-white rounded-2xl shadow-md border border-gray-100 p-5 md:p-6">
            <ul className="list-disc pl-5 space-y-3 text-gray-800 text-sm md:text-base">
              <li>支払い方法：Stripeにてお支払いいただきます</li>
              <li>
                支払い時期：月額プランの場合は各プランの更新日に、回数チケットの場合は購入した時点でのお支払いとなります。
              </li>
            </ul>
          </div>
        </section>

        {/* Final CTA */}
        <section className="mx-auto max-w-3xl px-6 pb-16 pt-8 bg-[#FFFFFF]">
          <TrialCTASection isLoading={isLoading} onClick={startCheckout} asInline isReturning={hasEverSubscribed} />
        </section>

        {/* Mobile Fixed CTA */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-[100] bg-white/95 backdrop-blur border-t border-gray-200">
          <div className="mx-auto max-w-6xl px-4 py-2">
            <TrialCTASection isLoading={isLoading} onClick={startCheckout} asInline isReturning={hasEverSubscribed} />
          </div>
        </div>
      </div>
    </>
  )
}
